import { PagebuilderAlignOptions, PagebuilderHeadingTagOptions } from 'cms/constants/pagebuilder'
import {
  type PagebuilderImageAspectRatioSchema,
  PagebuilderSectionNames
} from 'cms/constants/pagebuilder'
import type {
  PagebuilderTilesGridSectionImageSettingsSchema,
  PagebuilderTilesGridSectionVariant
} from 'cms/schemaTypes/pagebuilder/documents/pagebuilderTilesGridSectionSchema'
import type { PagebuilderDescriptionFieldSchema } from 'cms/schemaTypes/pagebuilder/fields/description/pagebuilderDescriptionField'
import { type PagebuilderHeadingFieldSchema } from 'cms/schemaTypes/pagebuilder/fields/heading/pagebuilderHeadingFields'
import type { PagebuilderListFieldSchema } from 'cms/schemaTypes/pagebuilder/fields/list/pagebuilderListFieldset'
import type { PagebuilderSharedSectionSettingsSchema } from 'cms/schemaTypes/pagebuilder/fields/pagebuilderSectionSettings'
import type { PagebuilderButtonSchema } from 'cms/schemaTypes/pagebuilder/objects/pagebuilderButton'
import type { PagebuilderImageSchema } from 'cms/schemaTypes/pagebuilder/objects/pagebuilderImage'
import type { PagebuilderListItemSchema } from 'cms/schemaTypes/pagebuilder/objects/pagebuilderListItem'
import type { PagebuilderTileSchema } from 'cms/schemaTypes/pagebuilder/objects/pagebuilderTile'
import type { PagebuilderSectionSchema } from 'cms/schemaTypes/pagebuilder/pagebuilderPage'

import { urlFor } from 'lib/sanity/image'

import { GetPagebuilder } from './api'
import {
  type PagebuilderButtonViewModel,
  type PagebuilderDescriptionViewModel,
  type PagebuilderHeadingViewModel,
  type PagebuilderImageAspectRatio,
  type PagebuilderImageViewModel,
  type PagebuilderListItemViewModel,
  type PagebuilderListViewModel,
  type PagebuilderPageViewModel,
  type PagebuilderSectionSettings,
  type PagebuilderSectionViewModel,
  type PagebuilderTilesGridSectionImageSettingsDeviceViewModel,
  type PagebuilderTilesGridSectionImageSettingsViewModel,
  type PagebuilderTileViewModel
} from './types'

const pagebuilderTilesGridSectionVariantBoxed: PagebuilderTilesGridSectionVariant =
  'boxed'

type PagebuilderMaxContentWidthOptions = 'full' | 'default' | 'custom'

/**
 * Helper mappers
 */

const mapMaxContentWidth = (
  maxContentWidth?: PagebuilderMaxContentWidthOptions,
  maxContentWidthCustomValue?: number
): 'full' | number | undefined => {
  if (maxContentWidth === 'full') {
    return 'full'
  }

  if (
    maxContentWidth === 'custom' &&
    typeof maxContentWidthCustomValue === 'number' &&
    maxContentWidthCustomValue > 0
  ) {
    return maxContentWidthCustomValue
  }

  return undefined
}

const mapColorToHex = (
  color?: PagebuilderSharedSectionSettingsSchema['backgroundColor']
): string | undefined => {
  if (typeof color === 'string') {
    return color
  }

  if (color && typeof color.color === 'string') {
    return color.color
  }

  return undefined
}

const mapSectionSettings = (
  settings?: PagebuilderSharedSectionSettingsSchema
): PagebuilderSectionSettings | undefined => {
  if (!settings) return undefined

  return {
    backgroundType: settings.backgroundType,
    backgroundColor: mapColorToHex(settings.backgroundColor),
    backgroundImageUrl: mapBackgroundImageToUrl(settings.backgroundImage),
    backgroundImageFit: settings.backgroundImageFit,
    backgroundImagePosition: settings.backgroundImagePosition,
    textColor: mapColorToHex(settings.textColor),
    maxContentWidth: mapMaxContentWidth(
      settings.maxContentWidth,
      settings.maxContentWidthCustomValue
    ),
    wavedEdgesTop: settings.wavedEdges?.top,
    wavedEdgesBottom: settings.wavedEdges?.bottom,
    spacingDesktopPaddingLeft: settings.spacing?.desktop?.paddingX?.left,
    spacingDesktopPaddingRight: settings.spacing?.desktop?.paddingX?.right,
    spacingDesktopPaddingTop: settings.spacing?.desktop?.paddingY?.top,
    spacingDesktopPaddingBottom: settings.spacing?.desktop?.paddingY?.bottom,
    spacingDesktopMarginTop: settings.spacing?.desktop?.marginY?.top,
    spacingDesktopMarginBottom: settings.spacing?.desktop?.marginY?.bottom,
    spacingDesktopMarginTopValue: settings.spacing?.desktop?.marginY?.topValue,
    spacingDesktopMarginBottomValue:
      settings.spacing?.desktop?.marginY?.bottomValue,
    spacingTabletPaddingLeft: settings.spacing?.tablet?.paddingX?.left,
    spacingTabletPaddingRight: settings.spacing?.tablet?.paddingX?.right,
    spacingTabletPaddingTop: settings.spacing?.tablet?.paddingY?.top,
    spacingTabletPaddingBottom: settings.spacing?.tablet?.paddingY?.bottom,
    spacingTabletMarginTop: settings.spacing?.tablet?.marginY?.top,
    spacingTabletMarginBottom: settings.spacing?.tablet?.marginY?.bottom,
    spacingTabletMarginTopValue: settings.spacing?.tablet?.marginY?.topValue,
    spacingTabletMarginBottomValue:
      settings.spacing?.tablet?.marginY?.bottomValue,
    spacingMobilePaddingLeft: settings.spacing?.mobile?.paddingX?.left,
    spacingMobilePaddingRight: settings.spacing?.mobile?.paddingX?.right,
    spacingMobilePaddingTop: settings.spacing?.mobile?.paddingY?.top,
    spacingMobilePaddingBottom: settings.spacing?.mobile?.paddingY?.bottom,
    spacingMobileMarginTop: settings.spacing?.mobile?.marginY?.top,
    spacingMobileMarginBottom: settings.spacing?.mobile?.marginY?.bottom,
    spacingMobileMarginTopValue: settings.spacing?.mobile?.marginY?.topValue,
    spacingMobileMarginBottomValue:
      settings.spacing?.mobile?.marginY?.bottomValue,
    visibilityMobile: settings.visibility?.mobile,
    visibilityTablet: settings.visibility?.tablet,
    visibilityDesktop: settings.visibility?.desktop
  }
}

function mapBackgroundImageToUrl(
  backgroundImage?: PagebuilderSharedSectionSettingsSchema['backgroundImage']
): string | undefined {
  const source = backgroundImage?.asset

  if (!source) {
    return undefined
  }

  return urlFor(source).url()
}

const mapHeading = (
  enabled?: boolean,
  heading?: PagebuilderHeadingFieldSchema
): PagebuilderHeadingViewModel | undefined => {
  if (!enabled || !heading) return undefined

  return {
    content: heading.content,
    tag: heading.options?.tag,
    sizeDesktopMode: heading.options?.sizeDesktopMode,
    sizeDesktopCustomValue: heading.options?.sizeDesktopCustomValue,
    sizeMobileMode: heading.options?.sizeMobileMode,
    sizeMobileCustomValue: heading.options?.sizeMobileCustomValue,
    alignDesktop: heading.options?.alignDesktop,
    alignMobile: heading.options?.alignMobile,
    maxContentWidth: mapMaxContentWidth(
      heading.options?.maxContentWidth,
      heading.options?.maxContentWidthCustomValue
    )
  }
}

const mapDescription = (
  enabled?: boolean,
  description?: PagebuilderDescriptionFieldSchema
): PagebuilderDescriptionViewModel | undefined => {
  if (!enabled || !description) return undefined

  return {
    content: description.content,
    maxContentWidth: mapMaxContentWidth(
      description.options?.maxContentWidth,
      description.options?.maxContentWidthCustomValue
    ),
    alignDesktop: description.options?.alignDesktop,
    alignMobile: description.options?.alignMobile
  }
}

const mapListItems = (
  items?: PagebuilderListItemSchema[]
): PagebuilderListItemViewModel[] => {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    heading: item.heading,
    description: item.description,
    image: item.image
  }))
}

const mapList = (
  enabled?: boolean,
  list?: PagebuilderListFieldSchema
): PagebuilderListViewModel | undefined => {
  if (!enabled || !list) return undefined

  return {
    items: mapListItems(list.listItems),
    itemImageWidth: list.listItemImageWidth
  }
}

const mapButton = (
  enabled?: boolean,
  button?: PagebuilderButtonSchema
): PagebuilderButtonViewModel | undefined => {
  const buttonLabel = button?.label
  const buttonVariant = button?.variant
  const buttonSize = button?.size
  const buttonLink = button?.link

  if (!enabled || !buttonLabel || !buttonLink) return undefined

  return {
    content: {
      label: buttonLabel,
      variant: buttonVariant,
      size: buttonSize,
      link: buttonLink
    },
    desktopPlacement: button.desktopPlacement,
    mobilePlacement: button.mobilePlacement
  }
}

const mapImage = (
  image?: PagebuilderImageSchema
): PagebuilderImageViewModel | undefined => {
  if (!image) return undefined

  return {
    alt: image.alt || image.desktop?.alt,
    desktop: image.desktop,
    mobile: image.mobile || image.desktop, // If mobile image is not set, use desktop image
    link: image.link,
    isRounded: Boolean(image.isRounded)
  }
}

const mapTile = (tile: PagebuilderTileSchema): PagebuilderTileViewModel => {
  const mappedHeading = mapHeading(tile.isHeadingEnabled, tile.heading)

  return {
    image: tile.isImageEnabled ? mapImage(tile.image) : undefined,
    heading: mappedHeading
      ? {
          ...mappedHeading,
          tag: 'h3' as PagebuilderHeadingTagOptions
        }
      : undefined,
    description: mapDescription(tile.isDescriptionEnabled, tile.description),
    button: mapButton(tile.isButtonEnabled, tile.button)
  }
}

const parseAspectRatio = (value: string) => {
  if (!value.includes('/')) {
    return undefined
  }

  const [widthPart, heightPart] = value.split('/')

  if (heightPart === undefined) {
    return undefined
  }

  const width = Number(widthPart.trim())
  const height = Number(heightPart.trim())

  // isFinite prevents NaN and Infinity values (divided by zero)
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    height <= 0 ||
    width <= 0
  ) {
    return undefined
  }

  return `${width}/${height}`
}

const mapAspectRatio = (
  value?: PagebuilderImageAspectRatioSchema
): PagebuilderImageAspectRatio | undefined => {
  if (!value) return undefined

  return {
    desktop: parseAspectRatio(value.desktop || ''),
    mobile: parseAspectRatio(value.mobile || value.desktop || '') // If mobile aspect ratio is not set, use desktop aspect ratio
  }
}

const mapTilesImageSettingsDevice = (
  value?: PagebuilderTilesGridSectionImageSettingsSchema['desktop']
): PagebuilderTilesGridSectionImageSettingsDeviceViewModel | undefined => {
  if (!value) return undefined

  const mappedValue = {
    width: value.imageWidth,
    aspectRatio: parseAspectRatio(value.aspectRatio || ''),
    placement: value.imagePlacement
  }

  return Object.values(mappedValue).every(item => item === undefined)
    ? undefined
    : mappedValue
}

const mapTilesImageSettings = (
  value?: PagebuilderTilesGridSectionImageSettingsSchema
): PagebuilderTilesGridSectionImageSettingsViewModel | undefined => {
  if (!value) return undefined

  const mappedValue = {
    desktop: mapTilesImageSettingsDevice(value.desktop),
    mobile: mapTilesImageSettingsDevice(value.mobile)
  }

  return mappedValue.desktop || mappedValue.mobile ? mappedValue : undefined
}

const mapGridGapValue = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return undefined
  }

  return value
}

/**
 * Core mappers - map CMS section data to result in form of the view model.
 */
const mapSection = (
  section: PagebuilderSectionSchema
): PagebuilderSectionViewModel | null => {
  const base = {
    id: section.id,
    settings: mapSectionSettings(section.sectionSettings)
  }

  switch (section?._type) {
    case PagebuilderSectionNames.pagebuilderBaseContentSection:
      return {
        ...base,
        _type: PagebuilderSectionNames.pagebuilderBaseContentSection,
        heading: mapHeading(section.isHeadingEnabled, section.heading),
        description: mapDescription(
          section.isDescriptionEnabled,
          section.description
        ),
        list: mapList(section.isListEnabled, section.list),
        button: mapButton(section.isButtonEnabled, section.button)
      }
    case PagebuilderSectionNames.pagebuilderSplitContentSection:
      return {
        ...base,
        _type: PagebuilderSectionNames.pagebuilderSplitContentSection,
        heading: mapHeading(section.isHeadingEnabled, section.heading),
        description: mapDescription(
          section.isDescriptionEnabled,
          section.description
        ),
        list: mapList(section.isListEnabled, section.list),
        button: mapButton(section.isButtonEnabled, section.button),
        image: mapImage(section.image),
        imageAspectRatio: mapAspectRatio(section.imageAspectRatio),
        imagePlacementDesktop: section.imagePlacementDesktop,
        imagePlacementMobile: section.imagePlacementMobile
      }
    case PagebuilderSectionNames.pagebuilderTilesGridSection:
      return {
        ...base,
        _type: PagebuilderSectionNames.pagebuilderTilesGridSection,
        heading: mapHeading(section.isHeadingEnabled, section.heading),
        description: mapDescription(
          section.isDescriptionEnabled,
          section.description
        ),
        tiles: (section.tiles || []).map(mapTile),
        columnsCount: section.columnsCount,
        gridGapRowMobile: mapGridGapValue(section.gridGapRowMobile),
        gridGapRowDesktop: mapGridGapValue(section.gridGapRowDesktop),
        gridGapColumnMobile: mapGridGapValue(section.gridGapColumnMobile),
        gridGapColumnDesktop: mapGridGapValue(section.gridGapColumnDesktop),
        tileVariant:
          section.tileVariant || pagebuilderTilesGridSectionVariantBoxed,
        mainButton: mapButton(section.isMainButtonEnabled, section.mainButton),
        imageSettings: mapTilesImageSettings(section.imageSettings)
      }
    case PagebuilderSectionNames.pagebuilderBannersGridSection:
      return {
        ...base,
        _type: PagebuilderSectionNames.pagebuilderBannersGridSection,
        heading: mapHeading(section.isHeadingEnabled, section.heading),
        description: mapDescription(
          section.isDescriptionEnabled,
          section.description
        ),
        banners: (section.banners || [])
          .map(mapImage)
          .filter((item): item is PagebuilderImageViewModel => Boolean(item)),
        columnsCount: section.columnsCount,
        gridGapRowMobile: mapGridGapValue(section.gridGapRowMobile),
        gridGapRowDesktop: mapGridGapValue(section.gridGapRowDesktop),
        gridGapColumnMobile: mapGridGapValue(section.gridGapColumnMobile),
        gridGapColumnDesktop: mapGridGapValue(section.gridGapColumnDesktop),
        mainButton: mapButton(section.isMainButtonEnabled, section.mainButton),
        imageAspectRatio: mapAspectRatio(section.imageAspectRatio)
      }
    case PagebuilderSectionNames.pagebuilderMarqueeSection:
      return {
        ...base,
        _type: PagebuilderSectionNames.pagebuilderMarqueeSection,
        isLabelEnabled: section.isLabelEnabled,
        label: section.label,
        isAssetEnabled: section.isAssetEnabled,
        asset: section.asset,
        overlayLink: section.overlayLink
      }
    case PagebuilderSectionNames.pagebuilderNewsletterFormSection:
      return {
        ...base,
        _type: PagebuilderSectionNames.pagebuilderNewsletterFormSection,
        heading: mapHeading(section.isHeadingEnabled, section.heading),
        description: mapDescription(
          section.isDescriptionEnabled,
          section.description
        )
      }
    default:
      return null
  }
}

export const mapPagebuilderSections = (
  sections?: PagebuilderSectionSchema[] | null
): PagebuilderSectionViewModel[] => {
  if (!sections?.length) return []

  return sections
    .map(section => mapSection(section))
    .filter((section): section is PagebuilderSectionViewModel =>
      Boolean(section)
    )
}

export const mapPagebuilderPage = (
  page?: GetPagebuilder | null
): PagebuilderPageViewModel | null => {
  if (!page) return null

  return {
    slug: page.slug?.current || '',
    seo: page.seo,
    sections: mapPagebuilderSections(page.sections)
  }
}

export const defaultPagebuilderAlign = PagebuilderAlignOptions.left

export const mapPagebuilderAlignToJustifyContent = (
  align: PagebuilderAlignOptions
) => {
  switch (align) {
    case PagebuilderAlignOptions.center:
      return 'center'
    case PagebuilderAlignOptions.right:
      return 'flex-end'
    case PagebuilderAlignOptions.left:
    default:
      return 'flex-start'
  }
}
